import { Notification } from '@thanx/uikit/notification'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import CmsPage from 'scenes/Cms/components/CmsPage'
import NotifyLocationManagersToggle from 'scenes/RecoveryAI/components/NotifyLocationManagersToggle'
import RecipientPicker from 'scenes/RecoveryAI/components/RecipientPicker'
import { EscalationContactsController } from 'scenes/RecoveryAI/components/useEscalationContactsController'

import { useSettingsEditPermission } from './hooks/useSettingsEditPermission'
import { useWrittenFeedbackPersisted } from './hooks/useWrittenFeedbackEnabled'
import { PANE_TITLE_STYLE } from './paneTitleStyle'
import WrittenFeedbackGateNote from './WrittenFeedbackGateNote'

const t = buildTranslate('order_ratings.settings')
// R3 hazard 5: a pre-existing, documented wart, left alone on purpose. The
// recipient labels below predate this pane (they came with the modal it
// replaces) and were never renamed off this namespace when the page split —
// see orderRatings.ts's own header comment on notification_settings.
const tNotify = buildTranslate('order_ratings.notification_settings')
// The toggle's own visible label ("Notify location managers") and the invite
// affordance's copy resolve from here, same wart, one level further in — see
// NotifyLocationManagersToggle/RecipientPicker, which read this namespace
// directly rather than accept a label override for either string.
const tEscalation = buildTranslate('recovery_ai.settings.escalation_contacts')
// Shared with the deleted modal's load-error copy: generic and not
// order-feedback-specific, so it was never worth forking.
const tModal = buildTranslate('recovery_ai.config_modal')

const GATE_NOTE_ID = 'notifications-gate-note'
// Stable and unique so a concurrent mount of RecoveryAI's own
// EscalationContactsConfigRow (a separate surface sharing this same
// controller/pickers) can never collide with this pane's DOM ids — R3
// hazard 6.
const INSTANCE_ID = 'order-feedback-notifications'

// Measured off prototype v67's own computed styles (R1 sec 3.3).
const SECTION_HEADING_FONT_SIZE = '18px'
const SECTION_HEADING_LINE_HEIGHT = '22.5px'
const SECTION_HEADING_LETTER_SPACING = '-0.2px'
const SECTION_DESC_FONT_SIZE = '13.5px'
const SECTION_DESC_LINE_HEIGHT = '20.25px'
// R3 hazard 3: NotifyLocationManagersToggle and RecipientPicker's floating
// panel were only ever exercised inside a 600-780px modal column. Capping
// each section at the mock's own measured `.nsec2` width (804px) keeps both
// components from stretching edge-to-edge at the Settings pane's wider real
// column, without touching either component's own internals.
const SECTION_MAX_WIDTH = '804px'

type Props = {
  // AI-1284: hoisted to the Settings container, which calls
  // useEscalationContactsController exactly ONCE and passes the result down.
  // The controller's own dirty/edit state (primaryIds, additionalIds,
  // notifyDraft) lives in local useState, not redux — a second call here
  // would be a SEPARATE instance with its OWN local state, unable to see an
  // edit the container's instance (the one Publish actually commits) knows
  // about.
  contacts: EscalationContactsController
}

// FR-24. Relocates OrderFeedbackNotificationsModal's controls (deleted with
// this pane) into the fourth Settings sub-tab: who gets emailed when guests
// leave feedback. Same data layer, same shared components — see the commit
// message for the full list of what moved and what changed.
const NotificationsPane = ({ contacts }: Props) => {
  const [css] = useStyletron()

  // The PERSISTED value, not the live one: measured on prototype v70, the
  // dependent panes are untouched while the Feedback collection toggle is
  // merely staged, and gate only once Publish lands.
  const writtenFeedbackEnabled = useWrittenFeedbackPersisted()
  const { editDisabled } = useSettingsEditPermission()

  const gated = !writtenFeedbackEnabled
  // R1 sec 3.5: both sections fade and go inert TOGETHER (unlike Guest
  // experience, nothing here is independent of the gate), so one boolean
  // covers both. isNotifySaving is omitted deliberately — it only ever fires
  // in 'eager' mode, which this pane never uses; isBusy alone already covers
  // a write in flight for both controls under 'staged'.
  const controlsDisabled = editDisabled || gated || contacts.isBusy
  const pickersDisabled = controlsDisabled || contacts.recipientsReadOnly

  const sectionHeadingClass = css({
    fontSize: SECTION_HEADING_FONT_SIZE,
    fontWeight: 800,
    lineHeight: SECTION_HEADING_LINE_HEIGHT,
    letterSpacing: SECTION_HEADING_LETTER_SPACING,
    color: palette.grey90,
  })
  const sectionDescClass = css({
    fontSize: SECTION_DESC_FONT_SIZE,
    lineHeight: SECTION_DESC_LINE_HEIGHT,
    color: palette.grey70,
    marginTop: '5px',
  })
  const sectionStyle: React.CSSProperties = {
    maxWidth: SECTION_MAX_WIDTH,
    opacity: controlsDisabled ? 0.5 : 1,
  }

  return (
    <CmsPage
      title={t('notifications.title')}
      titleClassName={css(PANE_TITLE_STYLE)}
      description={t('notifications.subtitle')}
      isLoading={contacts.isLoading}
    >
      {gated && (
        <div className="mb-m">
          <WrittenFeedbackGateNote id={GATE_NOTE_ID}>
            {t('notifications.gate_note.prefix')}{' '}
            <b>{t('written_feedback_gate.phrase')}</b>{' '}
            {t('notifications.gate_note.suffix')}
          </WrittenFeedbackGateNote>
        </div>
      )}
      {contacts.isUsersError ? (
        <Notification kind="negative" size="full">
          {tModal('load_error')}
        </Notification>
      ) : (
        <>
          {contacts.isSaveError && (
            <div className="mb-m">
              <Notification kind="negative" size="full">
                {contacts.isNotifyOnlySaveError
                  ? tNotify('notify_save_error')
                  : tNotify('save_error')}
              </Notification>
            </div>
          )}
          {contacts.adminGroupingUnavailable && (
            <div className="mb-m">
              <Notification kind="info" size="full">
                {tEscalation('admin_grouping_unavailable')}
              </Notification>
            </div>
          )}
          {contacts.contactsUnavailable && (
            <div className="mb-m">
              <Notification kind="warning" size="full">
                {contacts.recipientsReadOnly
                  ? tNotify('unloadable_notice')
                  : tNotify('unavailable_notice')}
              </Notification>
            </div>
          )}
          <div style={sectionStyle}>
            <b className={sectionHeadingClass}>
              {t('notifications.location_managers.heading')}
            </b>
            <p className={sectionDescClass}>
              {t('notifications.location_managers.description')}
            </p>
            <div className="mt-s">
              <NotifyLocationManagersToggle
                instanceId={INSTANCE_ID}
                value={contacts.notifyLocationManagers}
                onChange={contacts.onNotifyChange}
                disabled={controlsDisabled}
                ariaDescribedBy={gated ? GATE_NOTE_ID : undefined}
                // The design's own section description above already states
                // what this toggle does (R1 sec 3.2 has no description slot
                // inside the card row) — an empty override suppresses the
                // component's own RAI-flavored default rather than showing
                // two descriptions that say almost the same thing.
                description=""
                // This pane's card puts the label first and the switch pinned
                // to the right edge — the reverse of RAI's own layout.
                labelFirst
              />
            </div>
          </div>
          {/* Border shorthands so only the top edge draws, regardless of the
              <hr> UA/Bootstrap defaults. Spacing follows the prototype's own
              rule (border-top 1px solid grey30; margin-top: 34px; padding-top:
              30px) rather than the InnerTabs tab-divider convention briefed
              before the design spec landed — the 30px gap below is a
              padding-top on the hr itself, not a margin-bottom, so it is set
              explicitly to 0 here rather than left to Bootstrap's `hr { margin:
              1rem 0 }` default. */}
          <hr
            aria-hidden="true"
            className={css({
              height: '0px',
              borderWidth: '1px 0 0 0',
              borderStyle: 'solid',
              borderColor: palette.grey30,
              opacity: 1,
              margin: '34px 0 0',
              paddingTop: '30px',
            })}
            style={sectionStyle}
          />
          <div className="mb-l" style={sectionStyle}>
            <b className={sectionHeadingClass}>
              {t('notifications.recipients.heading')}
            </b>
            <div className="mt-s">
              <RecipientPicker
                instanceId={`${INSTANCE_ID}-primary`}
                mode="single"
                users={contacts.users}
                isAdmin={contacts.isUserAdmin}
                value={contacts.primaryIds}
                onChange={contacts.onPrimaryChange}
                label={
                  contacts.notifyLocationManagers
                    ? tNotify('fallback_label')
                    : tNotify('all_label')
                }
                // The section heading above already introduces this field, so
                // the picker's own label block would be redundant — `label`
                // stays the accessible name via the trigger's aria-label.
                hideLabel
                disabled={pickersDisabled}
                inviteAffordance="new-tab"
                showInviteIcon={false}
                // Design: the collapsed primary picker badges an admin
                // recipient "ACCOUNT ADMIN" after their name. The
                // additional-recipients picker below keeps today's
                // rendering (prop omitted → default off).
                showAdminBadge
                adminBadgeLabel={tNotify('account_admin_badge')}
                ariaDescribedBy={gated ? GATE_NOTE_ID : undefined}
              />
            </div>
            <div className="mt-xl">
              <RecipientPicker
                instanceId={`${INSTANCE_ID}-additional`}
                mode="multi"
                users={contacts.users}
                isAdmin={contacts.isUserAdmin}
                value={contacts.additionalIds}
                onChange={contacts.onAdditionalChange}
                label={tNotify('additional_label')}
                description={t('notifications.recipients.additional_desc')}
                disabled={pickersDisabled}
                ariaDescribedBy={gated ? GATE_NOTE_ID : undefined}
              />
            </div>
          </div>
        </>
      )}
    </CmsPage>
  )
}

export default NotificationsPane
